import * as yup from 'yup';
import _get from 'lodash/get';
import {
	Row,
	RenderConfig,
	TextInput,
	TextAreaInput,
	EMPTY_STRING,
	SelectInput,
	EMPTY_ARRAY,
	Column,
	CheckboxInput
} from 'commons-ui';
import {
	SEGMENT_NAME_VALIDATON_MESSAGE,
	ABTEST_NAME_VALIDATON_MESSAGE,
	ABTEST_NAME_PLACEHOLDER_MESSAGE,
	ZUPEE_USER_ID_DESCRIPTION_MESSAGE,
	ZUPEE_USER_ID_VALIDATION_MESSAGE,
	ESegmentType,
	EFrequencyType,
	MILLISECONDS_IN_29_DAYS,
	IS_RAPID
} from 'constant';

export const FORM_FIELD_KEYS = {
	_id: '_id',
	name: 'name',
	SegmentType: 'SegmentType',
	type: 'type',
	parentSegmentName: 'parentSegmentName',
	queryText: 'queryText',
	dagIds: 'dagIds',
	allowedABTestNames: 'allowedABTestNames',
	allowedZUserIds: 'allowedZUserIds',
	s3filePath: 's3filePath',
	isRapid: 'isRapid',
	createdBy: 'createdBy',
	createdAt: 'createdAt',
	updatedBy: 'updatedBy',
	updatedAt: 'updatedAt',
	scheduledAtTimestamp: 'scheduledAtTimestamp',
	useCase: 'useCase'
};

export const SCHEDULE_FORM_FIELD_KEYS = {
	_id: '_id',
	scheduleDate: 'scheduleDate',
	name: 'name',
	segmentType: 'segmentType',
	scheduledAtTimestamp: 'scheduledAtTimestamp',
	deleteCronJob: 'deleteCronJob',
	scheduleFrequency: 'scheduleFrequency',
	scheduleTime: 'scheduleTime',
	scheduleWeekDay: 'scheduleWeekDay'
};

const FREQUENCY_TYPE_OPTIONS = [
	{
		id: 'ONCE',
		label: 'ONCE'
	},
	{
		id: 'DAILY',
		label: 'DAILY'
	},
	{
		id: 'WEEKLY',
		label: 'WEEKLY'
	}
];

const EXECUTION_TYPE_OPTIONS = [
	{
		id: 'ATHENA',
		label: 'ATHENA'
	},
	{
		id: 'AWS_S3',
		label: 'AWS_S3'
	},
	{
		id: 'AIRFLOW',
		label: 'AIRFLOW'
	},
	{
		id: 'SCRIPT',
		label: 'SCRIPT'
	}
];

const SEGMENT_TYPE_OPTIONS = [
	{
		id: 'SEGMENT',
		label: 'SEGMENT'
	},
	{
		id: 'MULTI_SEGMENT',
		label: 'MULTI_SEGMENT'
	},
	{
		id: 'COMMON_SEGMENT',
		label: 'COMMON_SEGMENT'
	},
	{
		id: 'LIVE_SEGMENT',
		label: 'LIVE_SEGMENT'
	},
	{
		id: 'DERIVED_SEGMENT',
		label: 'DERIVED_SEGMENT'
	}
];

const WEEK_DAY_OPTIONS = [
	{ id: '0', label: 'Sunday' },
	{ id: '1', label: 'Monday' },
	{ id: '2', label: 'Tuesday' },
	{ id: '3', label: 'Wednesday' },
	{ id: '4', label: 'Thursday' },
	{ id: '5', label: 'Friday' },
	{ id: '6', label: 'Saturday' }
];

export const validationSchema = yup.object().shape(
	{
		name: yup
			.string()
			.required('Segment Name is a required field')
			.transform((value, originalValue) => {
				if (originalValue === null) {
					return EMPTY_STRING;
				}
				return value.trim();
			})
			.test('is-not-blank', 'Segment Name cannot be blank', (value) => {
				return value?.trim() !== EMPTY_STRING;
			})
			.test('valid-start-character', SEGMENT_NAME_VALIDATON_MESSAGE, (value) => {
				return typeof value === 'string' && /^[A-Za-z][A-Za-z0-9_]*$/.test(value);
			}),
		SegmentType: yup.string().required('Segment Type is a required field'),
		type: yup.string().required('Segment Execution Type is a required field'),
		dagIds: yup.mixed().transform((value) => {
			if (typeof value === 'string') {
				return value.split(',').map((item) => item.trim());
			}
			return value;
		}),
		useCase: yup
			.string()
			.required('Use Case is a required field')
			.transform((value, originalValue) => {
				if (originalValue === null) {
					return EMPTY_STRING;
				}
				return value.trim();
			})
			.test('is-not-blank', 'Use Case cannot be blank', (value) => {
				return value?.trim() !== EMPTY_STRING;
			}),
		isRapid: yup
			.boolean()
			.default(false) // Default value set to false
			.transform((value, originalValue) => (originalValue === null ? false : value)), // If null, set to false
		queryText: yup
			.string()
			.transform((value, originalValue) => {
				if (originalValue === null) {
					return EMPTY_STRING;
				}
				return value;
			})
			.when(['s3filePath'], {
				is: (s3filePath: string) => !s3filePath || s3filePath.trim() === EMPTY_STRING,
				then: yup.string().required('Query Text is a required field'),
				otherwise: yup.string()
			}),
		s3filePath: yup
			.string()
			.transform((value, originalValue) => {
				if (originalValue === null) {
					return EMPTY_STRING;
				}
				return value;
			})
			.when(['queryText'], {
				is: (queryText: string) => !queryText || queryText.trim() === EMPTY_STRING,
				then: yup.string().required('S3 file Path is a required field'),
				otherwise: yup.string().notRequired()
			}),
		allowedZUserIds: yup
			.string()
			.transform((value, originalValue) => {
				if (originalValue === null) {
					return EMPTY_STRING;
				}
				return value;
			})
			.test('valid-zupee-user-ids', ZUPEE_USER_ID_VALIDATION_MESSAGE, (value) => {
				if (value) {
					const digitRegex = /^[0-9]+$/;
					const IsAllValid = value.split(',').every((id) => {
						return digitRegex.test(id) && id.length === 15;
					});
					return IsAllValid;
				}
				return true;
			})
	},
	[['queryText', 's3filePath']]
);

export const editValidationSchema = yup.object().shape(
	{
		name: yup
			.string()
			.required('Segment Name is a required field')
			.transform((value, originalValue) => {
				if (originalValue === null) {
					return EMPTY_STRING;
				}
				return value.trim();
			})
			.test('is-not-blank', 'Segment Name cannot be blank', (value) => {
				return value?.trim() !== EMPTY_STRING;
			}),
		// .test('valid-start-character', `Segment Name must start with a letter,  and should contain only alphabetic letters , numbers , and '_'`, (value) => {
		// 	return typeof value === 'string' && /^[A-Za-z][A-Za-z0-9_]*$/.test(value);
		// })
		SegmentType: yup.string().required('Segment Type is a required field'),
		type: yup.string().required('Segment Execution Type is a required field'),
		dagIds: yup.mixed().transform((value) => {
			if (typeof value === 'string') {
				return value.split(',').map((item) => item.trim());
			}
			return value;
		}),
		useCase: yup
			.string()
			.required('Use Case is a required field')
			.transform((value, originalValue) => {
				if (originalValue === null) {
					return EMPTY_STRING;
				}
				return value.trim();
			})
			.test('is-not-blank', 'Use Case cannot be blank', (value) => {
				return value?.trim() !== EMPTY_STRING;
			}),
		queryText: yup
			.string()
			.transform((value, originalValue) => {
				if (originalValue === null) {
					return EMPTY_STRING;
				}
				return value;
			})
			.when(['s3filePath'], {
				is: (s3filePath: string) => !s3filePath || s3filePath.trim() === EMPTY_STRING,
				then: yup.string().required('Query Text is a required field'),
				otherwise: yup.string()
			}),
		s3filePath: yup
			.string()
			.transform((value, originalValue) => {
				if (originalValue === null) {
					return EMPTY_STRING;
				}
				return value;
			})
			.when(['queryText'], {
				is: (queryText: string) => !queryText || queryText.trim() === EMPTY_STRING,
				then: yup.string().required('S3 file Path is a required field'),
				otherwise: yup.string().notRequired()
			}),
		allowedZUserIds: yup
			.string()
			.transform((value, originalValue) => {
				if (originalValue === null) {
					return EMPTY_STRING;
				}
				return value;
			})
			.test('valid-zupee-user-ids', ZUPEE_USER_ID_VALIDATION_MESSAGE, (value) => {
				if (value) {
					const digitRegex = /^[0-9]+$/;
					const IsAllValid = value.split(',').every((id) => {
						return digitRegex.test(id) && id.length === 15;
					});
					return IsAllValid;
				}
				return true;
			})
	},
	[['queryText', 's3filePath']]
);

export const rapidSegmentEditResolver = (props: any, dependencyValues: Array<string>) => {
	const { name, setValue } = props;
	const isRapid: boolean = _get(props, ['initialValues', name], false);
	const segmentType: string = _get(dependencyValues, '1', '');

	// If segment is Live or Derived, disable editing and show tooltip
	if (segmentType === ESegmentType.DERIVED_SEGMENT || segmentType === ESegmentType.LIVE_SEGMENT) {
		setValue(IS_RAPID, false);
		return {
			disabled: true,
			toolTip: `Live and Derived segments can't be marked as Rapid`
		};
	}

	// If segment is not currently Rapid, prevent marking as Rapid
	if (!isRapid) {
		setValue(IS_RAPID, false);
		return {
			disabled: true,
			toolTip: 'This segment cannot be marked as Rapid'
		};
	}

	// Allow unchecking if segment is currently Rapid
	return { disabled: false };
};

const allowedZUserIdsDependencyResolver = (props: any, dependencyValues: unknown[]) => {
	const segmentType: ESegmentType = _get(dependencyValues, '0', '');
	const validSegmentTypes: ESegmentType[] = [ESegmentType.LIVE_SEGMENT, ESegmentType.DERIVED_SEGMENT];
	return { hidden: !validSegmentTypes.includes(segmentType) };
};

export const EDIT_FORM_FIELD_CONFIG = {
	[FORM_FIELD_KEYS._id]: new RenderConfig()
		.setName('_id')
		.setLabel('Id')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: false, edit: false }, required: false }),
	[FORM_FIELD_KEYS.name]: new RenderConfig()
		.setName('name')
		.setLabel('Segment Name')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: false, edit: false }, required: true }),
	[FORM_FIELD_KEYS.SegmentType]: new RenderConfig()
		.setName('SegmentType')
		.setLabel('Segment Type')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: false },
			required: true,
			options: SEGMENT_TYPE_OPTIONS
		}),
	[FORM_FIELD_KEYS.type]: new RenderConfig()
		.setName('type')
		.setLabel('Segment Execution Type')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true,
			options: EXECUTION_TYPE_OPTIONS
		}),
	[FORM_FIELD_KEYS.parentSegmentName]: new RenderConfig()
		.setName('parentSegmentName')
		.setLabel('Parent Segment Name')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: false, edit: true }, required: false }),
	[FORM_FIELD_KEYS.queryText]: new RenderConfig()
		.setName('queryText')
		.setLabel('Segment Query')
		.setRenderer(TextAreaInput)
		.setRenderProps({ isEditable: { create: false, edit: true }, required: false, minHeight: '300px' }),
	[FORM_FIELD_KEYS.dagIds]: new RenderConfig()
		.setName('dagIds')
		.setLabel('Please Enter dag_Ids ( Comma separated )')
		.setRenderer(TextAreaInput)
		.setRenderProps({ isEditable: { create: false, edit: true }, required: false, minHeight: '100px' }),
	[FORM_FIELD_KEYS.allowedABTestNames]: new RenderConfig()
		.setName('allowedABTestNames')
		.setLabel('Please enter the allowed AB segment names (comma-separated)')
		.setRenderer(TextAreaInput)
		.setDescription(ABTEST_NAME_VALIDATON_MESSAGE)
		.setRenderProps({
			isEditable: { create: false, edit: true },
			required: false,
			minHeight: '100px',
			placeholder: ABTEST_NAME_PLACEHOLDER_MESSAGE
		}),
	[FORM_FIELD_KEYS.allowedZUserIds]: new RenderConfig()
		.setName('allowedZUserIds')
		.setLabel('Please enter allowed zupee user Ids')
		.setRenderer(TextAreaInput)
		.setDescription(ZUPEE_USER_ID_DESCRIPTION_MESSAGE)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			dependencies: ['SegmentType'],
			dependencyResolver: allowedZUserIdsDependencyResolver,
			required: false,
			minHeight: '100px',
			placeholder: 'Eg:123456789012345,123456789012346'
		}),
	[FORM_FIELD_KEYS.s3filePath]: new RenderConfig()
		.setName('s3filePath')
		.setLabel('S3 File Path')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: false, edit: true }, required: false }),
	[FORM_FIELD_KEYS.useCase]: new RenderConfig()
		.setName('useCase')
		.setLabel('Use Case')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: false, edit: false }, required: true }),
	[FORM_FIELD_KEYS.createdBy]: new RenderConfig()
		.setName('createdBy')
		.setLabel('Created By')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: false, edit: false }, required: false }),
	[FORM_FIELD_KEYS.createdAt]: new RenderConfig()
		.setName('createdAt')
		.setLabel('Created At')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: false, edit: false },
			inputType: 'datetime-local',
			format: 'yyyy-dd-mm HH:MM:ss',
			required: true
		}),
	[FORM_FIELD_KEYS.updatedBy]: new RenderConfig()
		.setName('updatedBy')
		.setLabel('Updated By')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: false, edit: false }, required: false }),
	[FORM_FIELD_KEYS.updatedAt]: new RenderConfig()
		.setName('updatedAt')
		.setLabel('Updated At')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: false, edit: false },
			inputType: 'datetime-local',
			format: 'yyyy-dd-mm HH:MM:ss',
			required: true
		}),
	[FORM_FIELD_KEYS.scheduledAtTimestamp]: new RenderConfig()
		.setName('scheduledAtTimestamp')
		.setLabel('Segment Scheduled At')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: false, edit: false },
			required: false,
			isHidden: { create: true, edit: true }
		}),
	[FORM_FIELD_KEYS.isRapid]: new RenderConfig()
		.setName('isRapid')
		.setLabel('Is Rapid Segment')
		.setRenderer(CheckboxInput)
		.setRenderProps({ isEditable: { create: false, edit: false }, required: true })
		.setRenderProps({
			isEditable: { create: false, edit: false },
			required: true,
			dependencies: ['isRapid', 'SegmentType'],
			dependencyResolver: rapidSegmentEditResolver
		})
};

export const VIEW_FORM_FIELD_CONFIG = {
	[FORM_FIELD_KEYS._id]: new RenderConfig()
		.setName('_id')
		.setLabel('Id')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: false, edit: false, view: false }, required: false }),
	[FORM_FIELD_KEYS.name]: new RenderConfig()
		.setName('name')
		.setLabel('Segment Name')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: false, edit: false, view: false }, required: true }),
	[FORM_FIELD_KEYS.SegmentType]: new RenderConfig()
		.setName('SegmentType')
		.setLabel('Segment Type')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: false, edit: false, view: false }, required: true }),
	[FORM_FIELD_KEYS.type]: new RenderConfig()
		.setName('type')
		.setLabel('Segment Execution Type')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: false, edit: false, view: false },
			required: true,
			options: EXECUTION_TYPE_OPTIONS
		}),
	[FORM_FIELD_KEYS.parentSegmentName]: new RenderConfig()
		.setName('parentSegmentName')
		.setLabel('Parent Segment Name')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: false, edit: false, view: false }, required: false }),
	[FORM_FIELD_KEYS.queryText]: new RenderConfig()
		.setName('queryText')
		.setLabel('Segment Query')
		.setRenderer(TextAreaInput)
		.setRenderProps({ isEditable: { create: false, edit: false, view: false }, required: false, minHeight: '300px' }),
	[FORM_FIELD_KEYS.dagIds]: new RenderConfig()
		.setName('dagIds')
		.setLabel('Please Enter dag_Ids ( Comma separated )')
		.setRenderer(TextAreaInput)
		.setRenderProps({ isEditable: { create: false, edit: false, view: false }, required: false, minHeight: '100px' }),
	[FORM_FIELD_KEYS.allowedABTestNames]: new RenderConfig()
		.setName('allowedABTestNames')
		.setLabel('Please enter the allowed AB segment names (comma-separated)')
		.setRenderer(TextAreaInput)
		.setDescription(ABTEST_NAME_VALIDATON_MESSAGE)
		.setRenderProps({
			isEditable: { create: false, edit: false, view: false },
			required: false,
			minHeight: '100px',
			placeholder: ABTEST_NAME_PLACEHOLDER_MESSAGE
		}),
	[FORM_FIELD_KEYS.allowedZUserIds]: new RenderConfig()
		.setName('allowedZUserIds')
		.setLabel('Please enter allowed zupee user Ids')
		.setRenderer(TextAreaInput)
		.setRenderProps({
			isEditable: { create: false, edit: true, view: false },
			dependencies: ['SegmentType'],
			dependencyResolver: allowedZUserIdsDependencyResolver,
			required: false,
			minHeight: '100px',
			placeholder: 'Eg:123456789012345,123456789012346'
		}),
	[FORM_FIELD_KEYS.s3filePath]: new RenderConfig()
		.setName('s3filePath')
		.setLabel('S3 File Path')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: false, edit: false, view: false }, required: false }),
	[FORM_FIELD_KEYS.useCase]: new RenderConfig()
		.setName('useCase')
		.setLabel('Use Case')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: false, edit: false, view: false }, required: true }),
	[FORM_FIELD_KEYS.createdBy]: new RenderConfig()
		.setName('createdBy')
		.setLabel('Created By')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: false, edit: false, view: false }, required: false }),
	[FORM_FIELD_KEYS.createdAt]: new RenderConfig()
		.setName('createdAt')
		.setLabel('Created At')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: false, edit: false, view: false },
			inputType: 'datetime-local',
			format: 'yyyy-dd-mm HH:MM:ss',
			required: true
		}),
	[FORM_FIELD_KEYS.updatedBy]: new RenderConfig()
		.setName('updatedBy')
		.setLabel('Updated By')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: false, edit: false, view: false }, required: false }),
	[FORM_FIELD_KEYS.updatedAt]: new RenderConfig()
		.setName('updatedAt')
		.setLabel('Updated At')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: false, edit: false, view: false },
			inputType: 'datetime-local',
			format: 'yyyy-dd-mm HH:MM:ss',
			required: true
		}),
	[FORM_FIELD_KEYS.scheduledAtTimestamp]: new RenderConfig()
		.setName('scheduledAtTimestamp')
		.setLabel('Segment Scheduled At')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: false, edit: false, view: false },
			required: false,
			defaultValue: 'N/A',
			dependencies: ['SegmentType'],
			dependencyResolver: (props: any, dependencyValues: ESegmentType[]) => {
				const excludedSegmentTypes: ESegmentType[] = [ESegmentType.LIVE_SEGMENT, ESegmentType.DERIVED_SEGMENT];
				const segmentType: ESegmentType = _get(dependencyValues, '0', '');
				return {
					hidden: excludedSegmentTypes.includes(segmentType)
				};
			}
		}),
	[FORM_FIELD_KEYS.isRapid]: new RenderConfig()
		.setName('isRapid')
		.setLabel('Is Rapid Segment')
		.setRenderer(CheckboxInput)
		.setRenderProps({ isEditable: { create: false, edit: true, view: false }, required: true })
};

export const SCHEDULE_FORM_FIELD_CONFIG = {
	[SCHEDULE_FORM_FIELD_KEYS._id]: new RenderConfig()
		.setName('_id')
		.setLabel('Id')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: false, edit: false }, required: false }),
	[SCHEDULE_FORM_FIELD_KEYS.scheduledAtTimestamp]: new RenderConfig()
		.setName('scheduledAtTimestamp')
		.setLabel('Segment Already Scheduled At')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: false, edit: false }, required: false }),
	[SCHEDULE_FORM_FIELD_KEYS.scheduleDate]: new RenderConfig()
		.setName('scheduleDate')
		.setLabel('Schedule Date')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			inputType: 'date',
			min: new Date().toISOString().split('T')[0],
			max: new Date(new Date().getTime() + MILLISECONDS_IN_29_DAYS).toISOString().split('T')[0],
			required: true,
			defaultValue: '',
			value: '',
			dependencies: ['scheduleFrequency', 'deleteCronJob'],
			dependencyResolver: (props: any, dependencyValues: unknown[]) => ({
				hidden: _get(dependencyValues, '0', '') !== EFrequencyType.ONCE,
				required: !_get(dependencyValues, '1', false)
			}),
			onChange: (e: any) => {
				return e.target.value || '';
			}
		}),
	[SCHEDULE_FORM_FIELD_KEYS.scheduleTime]: new RenderConfig()
		.setName('scheduleTime')
		.setLabel('Schedule Time (hh:mm tt)')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			inputType: 'time',
			required: true,
			dependencies: ['deleteCronJob'],
			dependencyResolver: (props: Record<string, unknown>, dependencyValues: unknown[]) => {
				const deleteCronJob = dependencyValues[0];
				return {
					required: !deleteCronJob
				};
			}
		}),
	[SCHEDULE_FORM_FIELD_KEYS.scheduleWeekDay]: new RenderConfig()
		.setName('scheduleWeekDay')
		.setLabel('Day of Week')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true,
			options: WEEK_DAY_OPTIONS,
			dependencies: ['scheduleFrequency', 'deleteCronJob'],
			dependencyResolver: (props: any, dependencyValues: unknown[]) => ({
				hidden: _get(dependencyValues, '0', '') !== EFrequencyType.WEEKLY,
				required: !_get(dependencyValues, '1', false)
			})
		}),
	[SCHEDULE_FORM_FIELD_KEYS.name]: new RenderConfig()
		.setName('name')
		.setLabel('Segment Name')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: false, edit: false }, required: false }),
	[SCHEDULE_FORM_FIELD_KEYS.segmentType]: new RenderConfig()
		.setName('SegmentType')
		.setLabel('Segment Type')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: false, edit: false },
			required: false,
			options: SEGMENT_TYPE_OPTIONS
		}),
	[SCHEDULE_FORM_FIELD_KEYS.scheduleFrequency]: new RenderConfig()
		.setName('scheduleFrequency')
		.setLabel('Segment Run Frequency')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true,
			options: FREQUENCY_TYPE_OPTIONS,
			dependencies: ['deleteCronJob'],
			dependencyResolver: (props: Record<string, unknown>, dependencyValues: unknown[]) => {
				const deleteCronJob = dependencyValues[0];
				return {
					required: !deleteCronJob
				};
			}
		}),
	[SCHEDULE_FORM_FIELD_KEYS.deleteCronJob]: new RenderConfig()
		.setName('deleteCronJob')
		.setLabel('Delete Current Scheduled Cron Job')
		.setRenderer(CheckboxInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			overrides: { InputContainer: { style: () => ({ backgroundColor: 'white' }) } },
			checked: false
		})
};

export const isRapidDependencyResolver = (props: any, dependencyValues: unknown[]) => {
	const segmentType = _get(dependencyValues, '0', '');
	const isEditable = props.isEditable[props.mode];
	const { disabled, setValue } = props;

	// Uncheck and disable the checkbox if SegmentType is 'DERIVED_SEGMENT' or 'LIVE_SEGMENT'
	if ((segmentType === ESegmentType.DERIVED_SEGMENT || segmentType === ESegmentType.LIVE_SEGMENT) && isEditable && !disabled) {
		setValue(IS_RAPID, false);
		return { disabled: true, toolTip: 'You are not allowed to mark live and derived segments as Rapid.' };
	}

	return { disabled: false };
};

const executionTypeDependencyResolver = (props: any, dependencyValues: unknown[]) => {
	const segmentType = _get(dependencyValues, '0', '');
	let options = [];

	if ([ESegmentType.SEGMENT, ESegmentType.MULTI_SEGMENT, ESegmentType.COMMON_SEGMENT].includes(segmentType)) {
		options = EXECUTION_TYPE_OPTIONS.slice(0, 3); // Athena, AWS, Airflow
	} else {
		options = [{ id: 'SCRIPT', label: 'SCRIPT' }];
	}

	return { options };
};

export const CREATE_FORM_FIELD_CONFIG = {
	[FORM_FIELD_KEYS.name]: new RenderConfig()
		.setName('name')
		.setLabel(`Segment Name`)
		.setRenderer(TextInput)
		.setDescription(SEGMENT_NAME_VALIDATON_MESSAGE)
		.setRenderProps({
			isEditable: { create: true, edit: false },
			required: true,
			placeholder: SEGMENT_NAME_VALIDATON_MESSAGE
		}),
	[FORM_FIELD_KEYS.SegmentType]: new RenderConfig()
		.setName('SegmentType')
		.setLabel('Segment Type')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: false },
			required: true,
			options: SEGMENT_TYPE_OPTIONS
		}),
	[FORM_FIELD_KEYS.type]: new RenderConfig()
		.setName('type')
		.setLabel('Segment Execution Type')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: false },
			required: true,
			dependencies: ['SegmentType'],
			dependencyResolver: executionTypeDependencyResolver
		}),
	[FORM_FIELD_KEYS.parentSegmentName]: new RenderConfig()
		.setName('parentSegmentName')
		.setLabel('Parent Segment Name')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, required: false }),
	[FORM_FIELD_KEYS.useCase]: new RenderConfig()
		.setName('useCase')
		.setLabel('Use Case')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, required: true }),
	[FORM_FIELD_KEYS.queryText]: new RenderConfig()
		.setName('queryText')
		.setLabel('Segment Query')
		.setRenderer(TextAreaInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, required: false, minHeight: '300px' }),
	[FORM_FIELD_KEYS.dagIds]: new RenderConfig()
		.setName('dagIds')
		.setLabel('Please Enter dag_Ids ( Comma separated )')
		.setRenderer(TextAreaInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, required: false, minHeight: '100px' }),
	[FORM_FIELD_KEYS.allowedABTestNames]: new RenderConfig()
		.setName('allowedABTestNames')
		.setLabel('Please enter the allowed AB segment names (comma-separated)')
		.setRenderer(TextAreaInput)
		.setDescription(ABTEST_NAME_VALIDATON_MESSAGE)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: false,
			minHeight: '100px',
			placeholder: ABTEST_NAME_PLACEHOLDER_MESSAGE
		}),
	[FORM_FIELD_KEYS.allowedZUserIds]: new RenderConfig()
		.setName('allowedZUserIds')
		.setLabel('Please enter allowed zupee user Ids')
		.setRenderer(TextAreaInput)
		.setDescription(ZUPEE_USER_ID_DESCRIPTION_MESSAGE)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			dependencies: ['SegmentType'],
			dependencyResolver: allowedZUserIdsDependencyResolver,
			required: false,
			minHeight: '100px',
			placeholder: 'Eg:123456789012345,123456789012346'
		}),
	[FORM_FIELD_KEYS.s3filePath]: new RenderConfig()
		.setName('s3filePath')
		.setLabel('S3 File Path')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, required: false }),
	[FORM_FIELD_KEYS.isRapid]: new RenderConfig()
		.setName('isRapid')
		.setLabel('Is Rapid Segment')
		.setRenderer(CheckboxInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			dependencies: ['SegmentType'],
			dependencyResolver: isRapidDependencyResolver
		})
		.setToolTip('Determines if the segment is rapid')
};

export const FORM_LAYOUT_CONFIG = [
	new Row(FORM_FIELD_KEYS.name),
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.SegmentType)]), new Column([new Row(FORM_FIELD_KEYS.type)])]),
	new Row(FORM_FIELD_KEYS.parentSegmentName || EMPTY_STRING),
	new Row(FORM_FIELD_KEYS.queryText || EMPTY_STRING),
	new Row(FORM_FIELD_KEYS.dagIds || EMPTY_STRING),
	new Row(FORM_FIELD_KEYS.allowedABTestNames || EMPTY_STRING),
	new Row(FORM_FIELD_KEYS.allowedZUserIds || EMPTY_STRING),
	new Row(FORM_FIELD_KEYS.s3filePath || EMPTY_STRING),
	new Row(FORM_FIELD_KEYS.useCase || EMPTY_STRING),
	new Row(FORM_FIELD_KEYS.createdBy),
	new Row(FORM_FIELD_KEYS.createdAt),
	new Row(FORM_FIELD_KEYS.updatedBy),
	new Row(FORM_FIELD_KEYS.updatedAt),
	new Row(FORM_FIELD_KEYS.scheduledAtTimestamp),
	new Row(FORM_FIELD_KEYS.isRapid)
];

export const CREATE_FORM_LAYOUT_CONFIG = [
	new Row(FORM_FIELD_KEYS.name),
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.SegmentType)]), new Column([new Row(FORM_FIELD_KEYS.type)])]),
	new Row(FORM_FIELD_KEYS.parentSegmentName || EMPTY_STRING),
	new Row(FORM_FIELD_KEYS.useCase || EMPTY_STRING),
	new Row(FORM_FIELD_KEYS.queryText || EMPTY_STRING),
	new Row(FORM_FIELD_KEYS.dagIds || EMPTY_STRING),
	new Row(FORM_FIELD_KEYS.allowedABTestNames || EMPTY_STRING),
	new Row(FORM_FIELD_KEYS.allowedZUserIds || EMPTY_STRING),
	new Row(FORM_FIELD_KEYS.s3filePath || EMPTY_STRING),
	new Row(FORM_FIELD_KEYS.isRapid)
];

export const SCHEDULE_FORM_LAYOUT_CONFIG = [
	new Row(SCHEDULE_FORM_FIELD_KEYS.scheduledAtTimestamp || 'NOT SCHEDULED SO FAR'),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(SCHEDULE_FORM_FIELD_KEYS.scheduleFrequency)]),
		new Column([new Row(SCHEDULE_FORM_FIELD_KEYS.scheduleTime)]),
		new Column([new Row(SCHEDULE_FORM_FIELD_KEYS.scheduleWeekDay)])
	]),
	new Row(SCHEDULE_FORM_FIELD_KEYS.scheduleDate),
	new Row(SCHEDULE_FORM_FIELD_KEYS.name),
	new Row(null, EMPTY_ARRAY, [new Column([new Row(SCHEDULE_FORM_FIELD_KEYS.segmentType)])]),
	new Row(SCHEDULE_FORM_FIELD_KEYS.deleteCronJob)
];

export const scheduleValidationSchema = yup.object().shape(
	{
		scheduleFrequency: yup.string().when('deleteCronJob', {
			is: false,
			then: yup.string().required('Frequency is required'),
			otherwise: yup.string().notRequired()
		}),
		scheduleDate: yup.string().when(['scheduleFrequency', 'deleteCronJob'], {
			is: (scheduleFrequency: string, deleteCronJob: boolean) => scheduleFrequency === EFrequencyType.ONCE && !deleteCronJob,
			then: yup.string().required('Date is required'),
			otherwise: yup.string().notRequired()
		}),
		scheduleTime: yup.string().when('deleteCronJob', {
			is: false,
			then: yup.string().required('Time is required'),
			otherwise: yup.string().notRequired()
		}),
		scheduleWeekDay: yup.string().when(['scheduleFrequency', 'deleteCronJob'], {
			is: (scheduleFrequency: string, deleteCronJob: boolean) => scheduleFrequency === EFrequencyType.WEEKLY && !deleteCronJob,
			then: yup.string().required('Day of week is required'),
			otherwise: yup.string().notRequired()
		}),
		deleteCronJob: yup.boolean().default(false)
	},
	[['scheduleFrequency', 'deleteCronJob']]
);
